// src/hooks/useDualCalculation.js
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { calculateCartV2 } from '../services/invoiceService';

/**
 * DUAL-CALCULATION STRATEGY HOOK
 * 
 * This hook implements the "best of both worlds" approach:
 * 1. Frontend calculates INSTANTLY using backend logic → Fast UI
 * 2. Backend validates independently (debounced) → Accurate results
 * 3. Compare before invoice generation → Catch discrepancies
 * 
 * Configuration:
 * - Debounce: 150ms (user specified)
 * - Mismatch threshold: $10.0 (user specified)
 * 
 * @param {Object} config
 * @param {Array} config.cartDetails - Cart items with details
 * @param {number} config.discountPercentage - Discount percentage (0-100)
 * @param {string} config.clientDatabaseName - Database name for backend
 * @param {string} config.invoiceType - Invoice type (A, B, C, X)
 * @param {boolean} config.enabled - Enable/disable calculation
 * @param {number} config.debounceDelay - Debounce delay in ms (default: 150)
 * @param {number} config.validationThreshold - Mismatch threshold in $ (default: 10.0)
 */
export const useDualCalculation = ({
    cartDetails = [],
    discountPercentage = 0,
    clientDatabaseName,
    invoiceType = 'C',
    enabled = true,
    debounceDelay = 150,
    validationThreshold = 10.0,
    // Volume pricing support
    getDisplayRetailPrice,
    isVolumePricingActive,
    getVolumePrice,
    volumePricingStates = {},
    volumePricingConfig = {},
    volumePricingVersion = 0
}) => {
    // State for frontend calculation (instant)
    const [frontendCalculation, setFrontendCalculation] = useState(null);
    
    // State for backend validation (debounced)
    const [backendCalculation, setBackendCalculation] = useState(null);
    const [isValidating, setIsValidating] = useState(false);
    const [validationError, setValidationError] = useState(null);
    const [isValidated, setIsValidated] = useState(false);
    
    // State for mismatch detection
    const [mismatchDetected, setMismatchDetected] = useState(false);
    const [mismatchDetails, setMismatchDetails] = useState(null);
    
    // Refs for debouncing and request cancellation
    const debounceTimerRef = useRef(null);
    const abortControllerRef = useRef(null);
    const calculationVersionRef = useRef(0);
    const volumePricingStatesRef = useRef(volumePricingStates);
    
    // Keep ref in sync with latest volumePricingStates
    useEffect(() => {
        volumePricingStatesRef.current = volumePricingStates;
    }, [volumePricingStates]);
    
    // Create wrapper that reads from ref for backend validation
    const isVolumePricingActiveFromRef = useCallback((productId) => {
        return volumePricingStatesRef.current[productId] || false;
    }, []);
    
    /**
     * Frontend calculation using BACKEND LOGIC
     * This is the key: frontend uses same logic as backend
     */
    const calculateFrontend = useCallback((items, discount) => {
        if (!items || items.length === 0) {
            return null;
        }
        
        try {
            let subtotalSinIva = 0;
            const itemDetails = [];
            
            // Calculate each item
            items.forEach(item => {
                const quantity = parseFloat(item.quantity) || 0;
                const splitter = parseFloat(item.splitter) || 1;
                const actualQuantity = quantity / splitter;
                
                // Get base price
                let basePrice = item.RetailPrice;
                
                // Apply volume pricing if active
                if (typeof getDisplayRetailPrice === 'function' && 
                    typeof isVolumePricingActive === 'function' && 
                    typeof getVolumePrice === 'function') {
                    
                    const displayPrice = getDisplayRetailPrice(item.RetailPrice);
                    // Use ref to get latest volume pricing state
                    const isVolumeActive = volumePricingStatesRef.current[item.productId] || false;
                    
                    if (isVolumeActive) {
                        basePrice = getVolumePrice(displayPrice, item.productId, quantity);
                        console.log(`⚡ [Frontend Calc] Volume pricing applied to ${item.ProductCode}:`, {
                            original: item.RetailPrice,
                            displayPrice: displayPrice,
                            quantity: quantity,
                            volumeAdjusted: basePrice
                        });
                    } else {
                        basePrice = displayPrice;
                    }
                }
                
                // Get price without IVA
                let precioSinIva = 0;
                if (basePrice) {
                    // Price includes IVA, remove it
                    precioSinIva = parseFloat(basePrice) / 1.21;
                } else if (item.precio_sin_iva) {
                    precioSinIva = parseFloat(item.precio_sin_iva);
                } else {
                    console.warn('Item missing price:', item);
                    return;
                }
                
                const itemSubtotalSinIva = precioSinIva * actualQuantity;
                subtotalSinIva += itemSubtotalSinIva;
                
                itemDetails.push({
                    productCode: item.ProductCode,
                    productName: item.ProductDescription || item.ProductName,
                    quantity: actualQuantity,
                    precioSinIva: precioSinIva,
                    subtotalSinIva: itemSubtotalSinIva
                });
            });
            
            // Apply discount to subtotal WITHOUT IVA (tax-compliant)
            const discountAmount = subtotalSinIva * (discount / 100);
            const subtotalAfterDiscount = subtotalSinIva - discountAmount;
            
            // Calculate IVA on discounted amount
            const iva = subtotalAfterDiscount * 0.21;
            const total = subtotalAfterDiscount + iva;
            
            console.log('🟢 [Frontend Calc] Instant calculation:', {
                subtotalSinIva: subtotalSinIva.toFixed(2),
                discount: `${discount}%`,
                discountAmount: discountAmount.toFixed(2),
                subtotalAfterDiscount: subtotalAfterDiscount.toFixed(2),
                iva: iva.toFixed(2),
                total: total.toFixed(2),
                source: 'frontend-instant'
            });
            
            return {
                subtotalSinIva: subtotalSinIva.toFixed(2),
                discount: discountAmount.toFixed(2),
                discountPercentage: discount,
                subtotalAfterDiscount: subtotalAfterDiscount.toFixed(2),
                iva: iva.toFixed(2),
                total: total.toFixed(2),
                itemDetails,
                calculationSource: 'frontend-instant',
                timestamp: Date.now()
            };
        } catch (error) {
            console.error('❌ [Frontend Calc] Error:', error);
            return null;
        }
    }, []);
    
    /**
     * Backend validation (debounced)
     */
    const validateWithBackend = useCallback(async (items, discount, version) => {
        if (!items || items.length === 0 || !clientDatabaseName) {
            return;
        }
        
        // Cancel previous request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        
        abortControllerRef.current = new AbortController();
        setIsValidating(true);
        setValidationError(null);
        
        try {
            console.log('🔵 [Backend Validation] Requesting validation...', {
                itemCount: items.length,
                discount: `${discount}%`,
                version,
                volumePricingSnapshot: Object.keys(volumePricingStatesRef.current).map(id => ({
                    productId: id,
                    active: volumePricingStatesRef.current[id]
                }))
            });
            
            const result = await calculateCartV2({
                cartDetails: items,
                discountPercentage: discount,
                clientDatabaseName,
                invoiceType,
                // Pass volume pricing functions for backend calculation
                getDisplayRetailPrice,
                isVolumePricingActive: isVolumePricingActiveFromRef, // Use ref wrapper
                getVolumePrice
            });
            
            // Check if this response is still relevant
            if (version !== calculationVersionRef.current) {
                console.log('⚠️ [Backend Validation] Outdated response, ignoring', {
                    responseVersion: version,
                    currentVersion: calculationVersionRef.current
                });
                return;
            }
            
            console.log('✅ [Backend Validation] Received:', {
                total: result.total,
                subtotal: result.subtotalSinIva,
                version
            });
            
            setBackendCalculation(result);
            setIsValidated(true);
            setValidationError(null);
            
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('🟡 [Backend Validation] Request cancelled');
                return;
            }
            
            console.error('❌ [Backend Validation] Error:', error);
            setValidationError(error.message || 'Validation failed');
            setIsValidated(false);
        } finally {
            if (version === calculationVersionRef.current) {
                setIsValidating(false);
            }
        }
    }, [clientDatabaseName, invoiceType]);
    
    /**
     * Compare frontend and backend calculations
     */
    const compareCalculations = useCallback((frontend, backend) => {
        if (!frontend || !backend) {
            return;
        }
        
        const frontendTotal = parseFloat(frontend.total);
        const backendTotal = parseFloat(backend.total);
        const difference = Math.abs(frontendTotal - backendTotal);
        
        console.log('🔍 [Comparison]', {
            frontend: frontendTotal.toFixed(2),
            backend: backendTotal.toFixed(2),
            difference: difference.toFixed(2),
            threshold: validationThreshold
        });
        
        if (difference > validationThreshold) {
            console.warn('⚠️ [Mismatch Detected] Difference exceeds threshold!', {
                difference: `$${difference.toFixed(2)}`,
                threshold: `$${validationThreshold.toFixed(2)}`,
                percentageDiff: `${((difference / backendTotal) * 100).toFixed(2)}%`
            });
            
            setMismatchDetected(true);
            setMismatchDetails({
                frontendTotal,
                backendTotal,
                difference,
                percentageDiff: (difference / backendTotal) * 100,
                threshold: validationThreshold
            });
        } else {
            console.log('✅ [Match] Calculations match within threshold');
            setMismatchDetected(false);
            setMismatchDetails(null);
        }
    }, [validationThreshold]);
    
    /**
     * Effect: Calculate frontend immediately when cart changes
     */
    useEffect(() => {
        if (!enabled || !cartDetails || cartDetails.length === 0) {
            setFrontendCalculation(null);
            setBackendCalculation(null);
            setIsValidated(false);
            setMismatchDetected(false);
            return;
        }
        
        // Instant frontend calculation
        const calc = calculateFrontend(cartDetails, discountPercentage);
        setFrontendCalculation(calc);
        
        // Reset validation state when volume pricing changes
        // This prevents showing stale backend results
        setBackendCalculation(null);
        setIsValidated(false);
        setMismatchDetected(false);
        setMismatchDetails(null);
        
    }, [enabled, cartDetails, discountPercentage, calculateFrontend, volumePricingVersion]);
    
    /**
     * Effect: Debounced backend validation
     */
    useEffect(() => {
        if (!enabled || !cartDetails || cartDetails.length === 0 || !clientDatabaseName) {
            return;
        }
        
        // Clear previous timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        
        // Increment version for this calculation
        calculationVersionRef.current += 1;
        const currentVersion = calculationVersionRef.current;
        
        console.log(`⏱️ [Debounce] Starting ${debounceDelay}ms timer (version ${currentVersion})`);
        
        // Start new timer
        debounceTimerRef.current = setTimeout(() => {
            validateWithBackend(cartDetails, discountPercentage, currentVersion);
        }, debounceDelay);
        
        // Cleanup
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [enabled, cartDetails, discountPercentage, clientDatabaseName, debounceDelay, validateWithBackend, volumePricingVersion]);
    
    /**
     * Effect: Compare calculations when both are available
     */
    useEffect(() => {
        if (frontendCalculation && backendCalculation && isValidated) {
            compareCalculations(frontendCalculation, backendCalculation);
        }
    }, [frontendCalculation, backendCalculation, isValidated, compareCalculations]);
    
    /**
     * Get validation status badge
     */
    const validationBadge = useMemo(() => {
        if (!enabled || !cartDetails || cartDetails.length === 0) {
            return { text: '', color: 'default', show: false };
        }
        
        if (isValidating) {
            return { text: 'Validating...', color: 'info', show: true };
        }
        
        if (validationError) {
            return { text: '⚠️ Validation failed', color: 'error', show: true };
        }
        
        if (mismatchDetected) {
            return { text: '⚠️ Mismatch detected', color: 'warning', show: true };
        }
        
        if (isValidated) {
            return { text: '✅ Validated', color: 'success', show: true };
        }
        
        return { text: 'Calculating...', color: 'default', show: true };
    }, [enabled, cartDetails, isValidating, validationError, mismatchDetected, isValidated]);
    
    /**
     * Check if safe to generate invoice
     */
    const isSafeToGenerateInvoice = useCallback(() => {
        // Not safe if no calculation
        if (!frontendCalculation) {
            return {
                safe: false,
                reason: 'No calculation available',
                warning: null
            };
        }
        
        // Not safe if validating
        if (isValidating) {
            return {
                safe: false,
                reason: 'Waiting for server validation...',
                warning: null
            };
        }
        
        // Not safe if validation failed
        if (validationError) {
            return {
                safe: false,
                reason: `Validation error: ${validationError}`,
                warning: null
            };
        }
        
        // Not safe if not validated yet
        if (!isValidated || !backendCalculation) {
            return {
                safe: false,
                reason: 'Server validation pending...',
                warning: null
            };
        }
        
        // Safe but with warning if mismatch detected
        if (mismatchDetected && mismatchDetails) {
            return {
                safe: true,
                reason: null,
                warning: `Difference detected: Frontend $${mismatchDetails.frontendTotal.toFixed(2)} vs Server $${mismatchDetails.backendTotal.toFixed(2)} (difference: $${mismatchDetails.difference.toFixed(2)}). Server value will be used for invoice.`
            };
        }
        
        // Safe!
        return {
            safe: true,
            reason: null,
            warning: null
        };
    }, [frontendCalculation, isValidating, validationError, isValidated, backendCalculation, mismatchDetected, mismatchDetails]);
    
    /**
     * Manual recalculation trigger
     */
    const recalculate = useCallback(() => {
        if (!enabled || !cartDetails || cartDetails.length === 0) {
            return;
        }
        
        console.log('🔄 [Manual Recalculation] Triggered by user');
        
        // Recalculate frontend
        const calc = calculateFrontend(cartDetails, discountPercentage);
        setFrontendCalculation(calc);
        
        // Trigger backend validation immediately (no debounce)
        calculationVersionRef.current += 1;
        validateWithBackend(cartDetails, discountPercentage, calculationVersionRef.current);
    }, [enabled, cartDetails, discountPercentage, calculateFrontend, validateWithBackend]);
    
    return {
        // Calculations
        frontendCalculation,
        backendCalculation,
        
        // Validation state
        isValidating,
        isValidated,
        validationError,
        
        // Mismatch detection
        mismatchDetected,
        mismatchDetails,
        
        // UI helpers
        validationBadge,
        
        // Safety check
        isSafeToGenerateInvoice,
        
        // Manual trigger
        recalculate,
        
        // Config
        config: {
            debounceDelay,
            validationThreshold,
            enabled
        }
    };
};

export default useDualCalculation;
